import { mergeAttributes, Node } from '@tiptap/react';

/**
 * The `@mention` node.
 *
 * Not Tiptap's official `@tiptap/extension-mention` — that package (and the
 * `@tiptap/suggestion` it depends on) is not installed, and this task adds no
 * new dependencies. What follows is the minimum needed to round-trip the same
 * JSON shape the API's allowlist accepts (see `apps/api/.../message-body.ts`,
 * the `mention` case): an inline, atomic, non-editable node carrying `id` and
 * `label`. The `@`-triggered popup that inserts it lives in `Composer.tsx`,
 * built directly on `editor.view.coordsAtPos` and `editorProps.handleKeyDown`
 * rather than the `Suggestion` utility, for the same reason.
 *
 * `atom: true` is what makes it a single unit for cursor and backspace
 * purposes — a mention is deleted whole, never character-by-character, which
 * matters because "id" is not visible text a person could otherwise edit down
 * to something that no longer matches the label next to it.
 */
export const MentionNode = Node.create({
  name: 'mention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-mention-id'),
        renderHTML: (attributes: Record<string, unknown>) => ({
          'data-mention-id': attributes['id'],
        }),
      },
      label: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-mention-label'),
        renderHTML: (attributes: Record<string, unknown>) => ({
          'data-mention-label': attributes['label'],
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-mention-id]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        // Tailwind utilities, not a bespoke CSS class — the house rule is
        // token classes only, and a plain `class="mention-chip"` here would
        // mean adding a rule to index.css, which is out of bounds for this
        // change (see the task's file-ownership list).
        class: 'rounded-full bg-accent-tint px-1.5 py-px text-accent font-medium whitespace-nowrap',
      }),
      `@${String(node.attrs['label'] ?? '')}`,
    ];
  },

  renderText({ node }) {
    return `@${String(node.attrs['label'] ?? '')}`;
  },
});
